import React, { useEffect, useState } from 'react';
import { API_BASE, authHeaders } from '../../utils/api';
import '../../styles/admin-common.css';
import './TopRecruiters.css';

const TopRecruiters = () => {
  const [recruiters, setRecruiters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({
    company_name: '',
    display_order: 0,
    logo: null,
  });
  const [statusFilter, setStatusFilter] = useState('all');
  
  const role = React.useMemo(() => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.role || null;
    } catch {
      return null;
    }
  }, []);
  
  const isSuperadmin = role === 'superadmin';
  const [editingId, setEditingId] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  useEffect(() => {
    fetchRecruiters();
  }, []);
  
  useEffect(() => {
    fetchRecruiters();
  }, [statusFilter]);

  const fetchRecruiters = async () => {
    setLoading(true);
    setError('');
    try {
      let url = `${API_BASE}/api/admin/top-recruiters`;
      if (statusFilter !== 'all') {
        url += `?status=${encodeURIComponent(statusFilter)}`;
      }
      const res = await fetch(url, {
        headers: authHeaders(),
      });
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        throw new Error(`Server error: ${res.status} ${res.statusText}. ${text.substring(0, 80)}`);
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to load recruiters');
      setRecruiters(data.recruiters || []);
    } catch (err) {
      setError(err.message || 'Failed to load recruiters. Please check if the server is running.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 50 * 1024) {
        setError('Image size must be less than 50 KB');
        return;
      }
      setForm((prev) => ({ ...prev, logo: file }));
      const reader = new FileReader();
      reader.onloadend = () => setPreviewImage(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const resetForm = () => {
    setForm({ company_name: '', display_order: 0, logo: null });
    setEditingId(null);
    setPreviewImage(null);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!form.company_name) {
      setError('Company name is required');
      return;
    }

    const formData = new FormData();
    formData.append('company_name', form.company_name);
    formData.append('display_order', form.display_order || 0);
    if (form.logo) {
      formData.append('logo', form.logo);
    }

    try {
      const url = editingId
        ? `${API_BASE}/api/admin/top-recruiters/${editingId}`
        : `${API_BASE}/api/admin/top-recruiters`;
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: authHeaders(),
        body: formData,
      });

      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        throw new Error(`Server error: ${res.status} ${res.statusText}`);
      }

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to save recruiter');

      setSuccess(data.message || 'Recruiter saved successfully');
      resetForm();
      
      if (editingId) {
        // Force page reload after editing to show updated status
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        await fetchRecruiters();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (item) => {
    setForm({
      company_name: item.company_name,
      display_order: item.display_order,
      logo: null,
    });
    setEditingId(item.id);
    setPreviewImage(item.logo_path ? `${API_BASE}${item.logo_path}` : null);
    setError('');
    setSuccess('');
  };
  
  const handleApprove = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/top-recruiters/${id}/approve`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setSuccess('Recruiter approved successfully');
      await fetchRecruiters();
    } catch (err) {
      setError(err.message);
    }
  };
  
  const handleReject = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/top-recruiters/${id}/reject`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setSuccess('Recruiter rejected successfully');
      await fetchRecruiters();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this recruiter?')) return;
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/top-recruiters/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        throw new Error(`Server error: ${res.status} ${res.statusText}`);
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to delete recruiter');
      setSuccess('Recruiter deleted successfully');
      await fetchRecruiters();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="admin-page top-recruiters-page">
      <div className="page-heading">
      <h1>Top Recruiters</h1>
        <p>Manage company logos shown on the placements page.</p>
      </div>

      {error && <div className="admin-alert error">{error}</div>}
      {success && <div className="admin-alert success">{success}</div>}

      <div className="admin-grid recruiters-grid">
        <div className="card">
          <h2>{editingId ? 'Edit Recruiter' : 'Add Recruiter'}</h2>
          <form className="admin-form recruiter-form" onSubmit={handleSubmit}>
            <label>
              Company Name *
              <input
                type="text"
                name="company_name"
                value={form.company_name}
                onChange={handleChange}
                required
                placeholder="e.g., Maersk"
              />
            </label>

            <label>
              Display Order
              <input
                type="number"
                name="display_order"
                value={form.display_order}
                onChange={handleChange}
                min="0"
              />
            </label>



            <label>
              Company Logo (Max 50 KB)
              <input type="file" accept="image/*" onChange={handleImageChange} />
              {previewImage && (
                <div className="image-preview">
                  <img src={previewImage} alt="Preview" />
                  <p>Preview (resized to 240x120)</p>
                </div>
              )}
            </label>

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingId ? 'Update Recruiter' : 'Add Recruiter'}
              </button>
              {editingId && (
                <button type="button" className="btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="card recruiter-list-card">
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px'}}>
            <h2>Recruiter Logos</h2>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{padding: '5px 10px', borderRadius: '4px', border: '1px solid #ddd'}}>
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          {loading ? (
            <p>Loading recruiters...</p>
          ) : recruiters.length === 0 ? (
            <p>No recruiters yet.</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Logo</th>
                  <th>Company</th>
                  <th>Order</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {recruiters.map((item) => (
                  <tr key={item.id}>
                    <td>
                      {item.logo_path ? (
                        <img
                          src={`${API_BASE}${item.logo_path}`}
                          alt={item.company_name}
                          className="recruiter-thumb"
                        />
                      ) : (
                        <div className="recruiter-thumb-placeholder">No Logo</div>
                      )}
                    </td>
                    <td>{item.company_name}</td>
                    <td>{item.display_order}</td>
                    <td>
                      <span className={`status status-${item.status}`}>{item.status}</span>
                    </td>
                    <td className="actions">
                      <button className="btn-edit" onClick={() => handleEdit(item)}>
                        ✏️ Edit
                      </button>
                      <button className="btn-delete" onClick={() => handleDelete(item.id)}>
                        🗑️ Delete
                      </button>
                      {isSuperadmin && item.status === 'pending' && (
                        <button className="btn-success" onClick={() => handleApprove(item.id)}>
                          ✅ Approve
                        </button>
                      )}
                      {isSuperadmin && item.status === 'pending' && (
                        <button className="btn-danger" onClick={() => handleReject(item.id)}>
                          ❌ Reject
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopRecruiters;
